/**
 * The leader's event roster — TROOP-130.
 *
 * One read for the whole table: a column per ask, a row per person, and a cell where the two meet.
 * There is deliberately no `/family` half — this is the completion grid widened to every ask at
 * once, so it is `RequirementVisibilityPolicy`'s answer and a guardian is not an adult officer of
 * the unit. Rule 13b is about not building a *guardian's* feature under `/units/`; this is a
 * leader's feature and belongs where leaders are.
 */

import { API_BASE_URL, ApiError } from './client';
import type { CompletionSummary } from './requirements';

export interface RosterColumn {
  requirementId: string;
  /** Document | ExternalLink | Acknowledge | Payment. Decides which control the cell offers. */
  kind: string;
  title: string;
  /** Youth | Adults | AllParticipants. Cells outside it are greyed, never left blank. */
  appliesTo: string;
  isRequired: boolean;
}

export interface RosterCell {
  completionId: string;
  /**
   * Artifact | PhysicalCopy | SelfAttested. **Never rendered identically.** A scan anybody
   * entitled to it can open, a leader's word that the paper is in the binder, and a family's tick
   * are three different kinds of proof.
   */
  evidenceTier: string;
  storedFileId: string | null;
}

/** How to reach somebody. Null throughout unless contacts were asked for. */
export interface Contact {
  email: string | null;
  phone: string | null;
}

export interface GuardianContact {
  personId: string;
  displayName: string;
  email: string | null;
  phone: string | null;
}

export interface RosterPerson {
  personId: string;
  displayName: string;
  classification: string;
  /** Going | NotGoing | Maybe | null. Null is its own state and never means NotGoing. */
  rsvpResponse: string | null;
  /** Keyed by requirement id, so a column can be looked up rather than positionally matched. */
  cells: Record<string, RosterCell>;
  /**
   * Their own details, and **always null for a young person** — the server never sends one. Rule
   * 10: a leader who can contact a Scout off-system has one adult, no second adult and no record.
   */
  contact: Contact | null;
  /** How a leader reaches a young person — through the adults responsible for them. */
  guardians: GuardianContact[];
}

export interface EventRoster {
  columns: RosterColumn[];
  people: RosterPerson[];
  /** The unit's own word for its young people — "Scouts", "Cub Scouts" (rule 16). */
  youthLabelPlural: string;
}

export async function loadEventRoster(
  unitId: string,
  eventId: string,
  contacts = false,
): Promise<EventRoster> {
  // Asked for rather than always sent: a screen showing nobody's contact details should not have
  // fetched everybody's.
  const query = contacts ? '?contacts=true' : '';

  const response = await fetch(
    `${API_BASE_URL}/units/${unitId}/events/${eventId}/roster${query}`,
    { credentials: 'include' },
  );

  if (!response.ok) {
    const problem = await response.json().catch(() => null);

    throw new ApiError(
      response.status,
      problem?.detail ?? problem?.title ?? `Could not load the roster (${response.status}).`,
    );
  }

  return (await response.json()) as EventRoster;
}

/** Whether an ask reaches somebody at all. A cell outside it is greyed rather than left empty. */
export function askApplies(column: RosterColumn, classification: string): boolean {
  if (column.appliesTo === 'AllParticipants') return true;
  if (column.appliesTo === 'Youth') return classification === 'Youth';
  return classification === 'Adult';
}

/**
 * The three tier counts for every ask on this roster, derived from the rows already loaded --
 * TROOP-138.
 *
 * **The count and the checkbox that changes it are one fact, so they come from one piece of
 * state.** The setup section used to fetch `GET /requirements/{id}/summary` into its own
 * `useState`, keyed on an effect that could only fire on mount -- so ticking a cell in the grid
 * above left the line below reading whatever it had said when the block was opened, and a leader
 * deciding who was outstanding read a number the screen contradicted three inches higher. A
 * refetch would have been correct until the fifth mutation path forgot to call it; deriving makes
 * the lag unrepresentable.
 *
 * **It is not a second definition of who is expected.** `RequirementQueries.SummaryAsync` is
 * `grid.Count` plus three `Count(EvidenceTier == ...)` over `GridAsync`, whose `expected` CTE is
 * the same active-membership, effective-dated-classification population as {@link
 * EventRosterQueries.PeopleAsync} -- narrowed by `AppliesTo`, which is exactly what
 * {@link askApplies} already reads to decide whether a cell is greyed. This aggregates the rows on
 * screen the way the server aggregates the same rows.
 *
 * **Three numbers, never a total** (rule 15, §7). A scan anybody entitled to it can open, a
 * leader's word that the paper is in the binder, and a family's tick are different kinds of proof,
 * and the sum of any two of them has no honest definition.
 *
 * @param roster Every person, not the slice the table draws. The grid bounds itself at 25 rows and
 * holds all of them; a denominator taken from what is visible is the same class of confidently
 * wrong number this exists to prevent.
 * @returns Keyed by requirement id, so a setup card looks its own up rather than matching by
 * position.
 */
export function summariseRoster(roster: EventRoster): Record<string, CompletionSummary> {
  const summaries: Record<string, CompletionSummary> = {};

  for (const column of roster.columns) {
    const expected = roster.people.filter((p) => askApplies(column, p.classification));
    const tier = (name: string) =>
      expected.filter((p) => p.cells[column.requirementId]?.evidenceTier === name).length;

    summaries[column.requirementId] = {
      expected: expected.length,
      artifacts: tier('Artifact'),
      physicalCopies: tier('PhysicalCopy'),
      selfAttested: tier('SelfAttested'),
    };
  }

  return summaries;
}
